// Merchant documents (driver's license + voided check) an operator attaches on
// NewApplication. Optional on every application path -- the employee filling
// the form may have neither. The form holds the files until the application
// exists, then uploads each straight to S3 via a presigned PUT from
// generateApplicationDocumentUploadUrl -- the same storage the merchant's own
// intake upload uses, so either side sees the other's file.

import { gql } from "./api";
import { GENERATE_APPLICATION_DOCUMENT_UPLOAD_URL_MUTATION } from "./operations";
import type { ApplicationResult } from "./types";

export type DocumentKind = "DRIVERS_LICENSE" | "VOIDED_CHECK";

export const DOCUMENT_KINDS: { kind: DocumentKind; label: string }[] = [
  { kind: "DRIVERS_LICENSE", label: "Driver's license" },
  { kind: "VOIDED_CHECK", label: "Voided check" },
];

// Mirrors the backend's accepted types and 10MB cap.
export const DOCUMENT_CONTENT_TYPES = [
  "image/jpeg",
  "image/png",
  "application/pdf",
] as const;
export const DOCUMENT_MAX_BYTES = 10 * 1024 * 1024;

export type PendingDocuments = Partial<Record<DocumentKind, File>>;

/** Why a picked file can't be uploaded, or null when it can. */
export function documentFileProblem(file: File): string | null {
  if (!(DOCUMENT_CONTENT_TYPES as readonly string[]).includes(file.type))
    return "Use a JPEG, PNG, or PDF file.";
  if (file.size <= 0 || file.size > DOCUMENT_MAX_BYTES)
    return "The file must be 10MB or smaller.";
  return null;
}

export function documentLabel(kind: DocumentKind): string {
  return DOCUMENT_KINDS.find((d) => d.kind === kind)?.label ?? kind;
}

async function uploadOne(
  applicationID: string,
  kind: DocumentKind,
  file: File,
): Promise<boolean> {
  try {
    const data = await gql<{
      generateApplicationDocumentUploadUrl: ApplicationResult;
    }>(GENERATE_APPLICATION_DOCUMENT_UPLOAD_URL_MUTATION, {
      applicationID,
      kind,
      contentType: file.type,
      contentLength: file.size,
    });
    const result = data.generateApplicationDocumentUploadUrl;
    if (result.status !== "success" || !result.message) return false;
    const put = await fetch(result.message, {
      method: "PUT",
      headers: { "Content-Type": file.type },
      body: file,
    });
    return put.ok;
  } catch {
    return false;
  }
}

/** Upload every attached document; resolves to the kinds that failed. */
export async function uploadApplicationDocuments(
  applicationID: string,
  documents: PendingDocuments,
): Promise<DocumentKind[]> {
  const failed: DocumentKind[] = [];
  for (const { kind } of DOCUMENT_KINDS) {
    const file = documents[kind];
    if (file && !(await uploadOne(applicationID, kind, file)))
      failed.push(kind);
  }
  return failed;
}
